import {
  StatusBadge,
  CreatedByCell,
  GoalPerformanceCell,
  ChannelBadge,
  TagsCell,
  ActionDropdown,
  TitleCell,
  EnrollingCountCell,
} from './list-cell-renderers';
import { formatDateOrDash, parseUtcDate } from '../utils/cadence-data-mappers';

/**
 * Build MUI DataGrid column definitions for the Bulk Messages table.
 * Default visible: title, status, sendDate, channel, recipients, goalMetric, actions.
 * Hidden by default: cadenceShell, createdBy, createdDate, lastUpdated, rate columns, tags.
 */
export const createBulkMessagesColumns = ({ currentUserId, actions, renamingState }) => [
  {
    field: 'title',
    headerName: 'Title',
    flex: 1,
    minWidth: 200,
    sortable: true,
    renderCell: ({ row }) => (
      <TitleCell
        row={row}
        icon="email"
        onView={(id) => actions.onView('bulk-message', id)}
        renamingState={renamingState}
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 110,
    sortable: true,
    type: 'singleSelect',
    valueOptions: ['Draft', 'Sending', 'Sent', 'Scheduled', 'Paused', 'Archived'],
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
  },
  {
    field: 'cadenceShell',
    headerName: 'Cadence Blueprint',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    renderCell: ({ row }) => {
      const shellId = row._raw?.sourceCadenceTemplateId;
      if (!row.cadenceShell || !shellId) return '\u2014';
      return (
        <button
          type="button"
          className="cadences-v2-table--name-link"
          onClick={() => actions.onView('template', shellId)}
        >
          {row.cadenceShell}
        </button>
      );
    },
  },
  {
    field: 'channel',
    headerName: 'Channel',
    flex: 0.5,
    minWidth: 90,
    sortable: true,
    type: 'singleSelect',
    valueOptions: ['Email'],
    renderCell: ({ row }) => <ChannelBadge channel={row.channel} />,
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 0.7,
    minWidth: 120,
    sortable: true,
    valueGetter: (value) => value?.name || '',
    renderCell: ({ row }) => <CreatedByCell createdBy={row.createdBy} currentUserId={currentUserId} />,
  },
  {
    field: 'createdDate',
    headerName: 'Creation Date',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: parseUtcDate,
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'recipients',
    headerName: 'Recipients',
    flex: 0.5,
    minWidth: 90,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row, value }) => <EnrollingCountCell value={value} status={row.status} />,
  },
  {
    field: 'sendDate',
    headerName: 'Send Date',
    flex: 0.7,
    minWidth: 110,
    sortable: true,
    type: 'date',
    valueGetter: (value, row) => {
      const d = row.sentDate || row.scheduledDate;
      return parseUtcDate(d);
    },
    valueFormatter: (value) => (value ? formatDateOrDash(value) : '\u2014'),
  },
  {
    field: 'goalMetric',
    headerName: 'Goal Performance',
    flex: 1,
    minWidth: 200,
    sortable: true,
    renderCell: ({ row }) =>
      row.goalMetric !== null ? (
        <GoalPerformanceCell goalName={row.goal} metric={row.goalMetric} target={row.goalTarget} />
      ) : (
        <span className="cadences-v2-table--performance-pending">No data yet</span>
      ),
  },
  {
    field: 'deliveryRate',
    headerName: 'Delivery Rate',
    flex: 0.5,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'bounceRate',
    headerName: 'Bounce Rate',
    flex: 0.5,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'unsubscribeRate',
    headerName: 'Unsubscribe Rate',
    flex: 0.5,
    minWidth: 120,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'openRate',
    headerName: 'Open Rate',
    flex: 0.5,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'clickRate',
    headerName: 'Click Rate',
    flex: 0.5,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'replyRate',
    headerName: 'Reply Rate',
    flex: 0.5,
    minWidth: 100,
    sortable: true,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => (value != null ? `${value.toFixed(1)}%` : '\u2014'),
  },
  {
    field: 'tags',
    headerName: 'Tags',
    flex: 0.8,
    minWidth: 140,
    sortable: false,
    valueGetter: (value, row) => (row._raw?.tags || []).join(', '),
    renderCell: ({ row }) => <TagsCell tags={row._raw?.tags} />,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    sortable: false,
    filterable: false,
    hideable: false,
    type: 'actions',
    getApplyQuickFilterFn: () => null,
    renderCell: ({ row }) => (
      <ActionDropdown type="bulk-message" row={row} actions={actions} currentUserId={currentUserId} />
    ),
  },
];
